import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { UseFormReturn } from 'react-hook-form';
import { LOGIC_TRANSLATION_KEYS } from '@admin/types/workspace/logic.ts';
import type { RenewalProgramFormSchema } from '@admin/components/renew-program/renewal-program-form-schema.ts';
import type { LogicWorkspaceWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';

interface RenewalProgramConfirmationStepContentProps {
  form: UseFormReturn<RenewalProgramFormSchema>;
  currentLogic: LogicWorkspaceWorkspaceReadModel;
}

/** Dernière étape : récapitulatif read-only avant POST /admin/logicsWorkspaces. */
export function RenewalProgramConfirmationStepContent({
  form,
  currentLogic
}: RenewalProgramConfirmationStepContentProps) {
  const { t } = useTranslation();
  const values = form.watch();
  const logicType = currentLogic?.logic?.type;
  const conversionMethodLabel =
    logicType && logicType in LOGIC_TRANSLATION_KEYS
      ? t(LOGIC_TRANSLATION_KEYS[logicType as keyof typeof LOGIC_TRANSLATION_KEYS])
      : (currentLogic?.logic?.name ?? '—');

  const startAtLabel = values.startAt ? format(values.startAt, 'dd/MM/yyyy') : '—';
  const endAtLabel = values.endAt ? format(values.endAt, 'dd/MM/yyyy') : '—';

  const pointExpirationLabel =
    values.pointExpirationType === 'fixed' && values.pointExpirationDate
      ? format(values.pointExpirationDate, 'dd/MM/yyyy')
      : values.pointExpirationType === 'yearEnd' && values.pointExpirationYearOffset !== undefined
        ? values.pointExpirationYearOffset === 0
          ? t('form.workspace.point.expiration.select.option.n')
          : t('form.workspace.point.expiration.select.option.n.plus', {
              offset: values.pointExpirationYearOffset
            })
        : '—';

  const isStepLogicType = logicType === LogicLogicReadModelType.STEP;
  const renewalSteps = values.steps ?? [];
  const isTargetLogicType =
    logicType === LogicLogicReadModelType.TARGET_PERF ||
    logicType === LogicLogicReadModelType.TARGET_POINT;
  const targets = currentLogic?.targets ?? [];
  const targetTypeLabel =
    logicType && logicType in LOGIC_TRANSLATION_KEYS
      ? t(LOGIC_TRANSLATION_KEYS[logicType as keyof typeof LOGIC_TRANSLATION_KEYS])
      : '—';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">{t('form.workspace.renewal.confirmation.title')}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('form.workspace.renewal.confirmation.paragraph1')}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('form.workspace.renewal.confirmation.paragraph2')}
        </p>
      </div>

      {/* Récapitulatif en lecture seule */}
      <div className="p-4 space-y-4 rounded-lg border bg-muted/30">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm font-medium text-muted-foreground">
            {t('form.workspace.convert.method.label')} :
          </span>
          <span className="text-sm">{conversionMethodLabel}</span>
        </div>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm font-medium text-muted-foreground">
            {t('form.workspace.operation.date.start')} :
          </span>
          <span className="text-sm">{startAtLabel}</span>
        </div>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm font-medium text-muted-foreground">
            {t('form.workspace.operation.date.end')} :
          </span>
          <span className="text-sm">{endAtLabel}</span>
        </div>
        {isStepLogicType && renewalSteps.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-muted-foreground">
              {t('form.workspace.renewal.steps.list.label')} :
            </span>
            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>{t('form.step.name.label')}</TableHead>
                    <TableHead>{t('form.step.point.label')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...renewalSteps]
                    .sort((a, b) => a.stepPoint - b.stepPoint)
                    .map((step) => (
                      <TableRow key={`${step.name}-${step.stepPoint}`}>
                        <TableCell className="font-medium">{step.name}</TableCell>
                        <TableCell>{pointsTranslation(t, step.stepPoint, true)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        {isTargetLogicType && targets.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-muted-foreground">
              {t('form.workspace.renewal.targets.list.label')} :
            </span>
            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>{t('form.target.reference.label')}</TableHead>
                    <TableHead>{t('form.target.type.label')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {targets.map((target, index) => (
                    <TableRow key={target.reference ?? index}>
                      <TableCell className="font-medium">{target.reference}</TableCell>
                      <TableCell>{targetTypeLabel}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm font-medium text-muted-foreground">
            {t('form.workspace.point.expiration.title')} :
          </span>
          <span className="flex items-center gap-1.5 text-sm">{pointExpirationLabel}</span>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        {t('form.workspace.renewal.confirmation.footer')}
      </p>
    </div>
  );
}
